import React, {Component} from 'react'
import {connect} from 'react-redux'
import classNames from 'classnames'
import {arrayOf, bool, func, number, shape, string} from 'prop-types'
import {ReactComponent as ProdDefaultIllustration} from '../../images/login_prod_default.svg'
import {ReactComponent as StagDefaultIllustration} from '../../images/login_stag_default.svg'
import loginBackgroundMobile from '../../images/login_mobile.png'
import {
  authenticate,
  authenticateFactorTwo,
  enableRequestingNewCode,
  getInstanceProperties,
  getPasswordPolicy,
  getSsoInformation,
  onUpdateView,
  requestCode,
  requestNewPassword,
  setAuditMessage,
  setNewPassword
} from './login.actionCreators'
import {extractInstanceProps, onStartUp, onSubmit} from './login.helper'

import WelcomeSection from './welcomeSection/welcomeSection.container'
import InputFields from './inputFields/inputFields.container'
import ActionSection from './actionSection/actionSection.container'
import CannotConnect from './cannotConnect/cannotConnect.presenter'
import ProdWarning from './welcomeSection/prodWarning/prodWarning.presenter'
import {mediaQueryMaxWidth} from './helpers/login.data'

const Login = class Login extends Component {
    componentDidMount = () => {
      const {mode, hasAuthFailed, username, onGetInstanceProperties, onGetPasswordPolicy, onGetSsoInformation, onRequestCode, onEnableRequestingNewCode, onResize} = this.props
      onStartUp({mode, hasAuthFailed, username, onGetInstanceProperties, onGetPasswordPolicy, onGetSsoInformation, onRequestCode, onEnableRequestingNewCode})
      const checkWidth = () => {
        if (window.matchMedia) {
          const match = window.matchMedia(`(max-width: ${mediaQueryMaxWidth})`)
          onResize({isMobile: match.matches})
        }
      }

      checkWidth()
      window.addEventListener('resize', checkWidth)
    }

  onSubmit = () => {
    const {mode, username, password, confirmPassword, passwordPolicy, ssoInformation, secretCode, onAuthenticate, onAuthenticateFactorTwo, onRequestNewPassword, onSetNewPassword, onSubmitAuditMessage, isTFAEnabled, hasTFACookie, TFAChannel, auditMessage} = this.props
    onSubmit({mode, username, password, confirmPassword, passwordPolicy, ssoInformation, secretCode, onAuthenticate, onAuthenticateFactorTwo, onRequestNewPassword, onSetNewPassword, onSubmitAuditMessage, isTFAEnabled, hasTFACookie, TFAChannel, auditMessage})
  }

    render = () => {
      const {instanceProperties, ssoInformation, isInstanceAvailable, isMobile, username, password} = this.props
      const {prod} = extractInstanceProps(instanceProperties)
      const desktopBackground = prod ? <ProdDefaultIllustration /> : <StagDefaultIllustration />
      const loginIllustration = isMobile
        ? (<img alt='login' src={loginBackgroundMobile} />)
        : (desktopBackground)

      return (
        <div className='loginContainer' data-testid='login'>
          {isInstanceAvailable
            ? (
              <div className={classNames('loginPage', {prod, mobile: isMobile})}>
                <div className='loginImage'>
                  {loginIllustration}
                </div>

                <div className={classNames('loginFormWrapper', {prod, mobile: isMobile})}>
                  {prod && (
                    <ProdWarning />
                  )}
                  <div className={classNames('loginPanel', {prod})}>
                    <div className='loginForm'>
                      <WelcomeSection
                        isProduction={prod}
                      />
                      <InputFields
                        onSubmit={this.onSubmit}
                      />
                      <ActionSection
                        isProduction={prod}
                        onSubmit={this.onSubmit}
                        username={username}
                        password={password}
                        ssoInformation={ssoInformation}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
            : <CannotConnect />
          }
        </div>
      )
    }
}

const mapStateToProps = ({loginReducer}) => ({
  mode: loginReducer.mode,
  isInstanceAvailable: loginReducer.isInstanceAvailable,
  instanceProperties: loginReducer.instanceProperties,
  hasAuthFailed: loginReducer.hasAuthFailed,
  username: loginReducer.username,
  password: loginReducer.password,
  confirmPassword: loginReducer.confirmPassword,
  passwordPolicy: loginReducer.passwordPolicy,
  ssoInformation: loginReducer.ssoInformation,
  secretCode: loginReducer.secretCode,
  isTFAEnabled: loginReducer.isTFAEnabled,
  hasTFACookie: loginReducer.hasTFACookie,
  TFAChannel: loginReducer.TFAChannel,
  auditMessage: loginReducer.auditMessage,
  isMobile: loginReducer.isMobile
})

const mapDispatchToProps = dispatch => ({
  onAuthenticate: ({username, password}) => dispatch(authenticate({username, password})),
  onAuthenticateFactorTwo: ({username, twoFactorCode}) => dispatch(authenticateFactorTwo({username, code: twoFactorCode})),
  onRequestNewPassword: ({username}) => dispatch(requestNewPassword({username})),
  onSubmitAuditMessage: ({auditMessage}) => dispatch(setAuditMessage({auditMessage})),
  onSetNewPassword: ({username, password, confirmPassword, passwordPolicy, isTFAEnabled, hasTFACookie, TFAChannel}) => dispatch(setNewPassword({username, password, confirmPassword, passwordPolicy, isTFAEnabled, hasTFACookie, TFAChannel})),
  onGetInstanceProperties: () => dispatch(getInstanceProperties()),
  onGetPasswordPolicy: () => dispatch(getPasswordPolicy()),
  onGetSsoInformation: () => dispatch(getSsoInformation()),
  onRequestCode: ({username}) => dispatch(requestCode({username})),
  onEnableRequestingNewCode: () => dispatch(enableRequestingNewCode()),
  onResize: ({isMobile}) => dispatch(onUpdateView({isMobile}))
})

Login.propTypes = {
  hasAuthFailed: bool,
  mode: string,
  username: string,
  password: string,
  confirmPassword: string,
  passwordPolicy: shape({
    expireDays: number,
    minLength: number,
    minLowercase: number,
    minNumbers: number,
    minSpecial: number,
    minUpperCase: number
  }),
  ssoInformation: shape({
    ssoEnabled: bool,
    ssoClients: shape({
      clientRegistrations: shape(arrayOf(shape({
        registrationId: string,
        clientId: string,
        fromOidcIssuerLocation: string
      })))
    })
  }),
  secretCode: string,
  hasTFACookie: bool,
  instanceProperties: shape({
    name: string,
    type: string
  }),
  isInstanceAvailable: bool,
  isTFAEnabled: bool,
  TFAChannel: string,
  auditMessage: string,
  onAuthenticate: func,
  onAuthenticateFactorTwo: func,
  onRequestNewPassword: func,
  onSetNewPassword: func,
  onGetInstanceProperties: func,
  onGetPasswordPolicy: func,
  onGetSsoInformation: func,
  onRequestCode: func,
  onEnableRequestingNewCode: func,
  onSubmitAuditMessage: func,
  onResize: func,
  isMobile: bool
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
